/**
 * Tree view component for displaying registries grouped by year.
 * Uses HeadlessUI Disclosure for accessible expand/collapse functionality.
 * Optionally displays document types section above the registry tree.
 */

import type { Registry } from "@eoln/sdk-invoices";
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/react";
import { ChevronDown, ChevronRight, FileText, FolderOpen } from "lucide-react";
import type React from "react";
import { useCallback, useId } from "react";
import type { RegistryGroup } from "@/hooks/useRegistryTree";
import type { TabType } from "@/lib/url-state";
import { DocumentTypesSection } from "./DocumentTypesSection";

interface RegistryTreeProps {
  /** Registry groups organized by year */
  groups: RegistryGroup[];
  /** Currently selected registry ID (null for "All Registries") */
  selectedRegistryId: string | null;
  /** Callback when a registry is selected */
  onRegistrySelect: (registryId: string | null) => void;
  /** Map of registryId to invoice count */
  invoiceCounts: Map<string, number>;
  /** Total invoice count across all registries */
  totalCount?: number;
  /** Whether invoice counts are loading */
  isCountsLoading?: boolean;
  /** Search query for filtering */
  searchQuery?: string;
  /** Years that should be expanded by default */
  defaultExpandedYears?: number[];
  /** Currently selected document type (optional, shows document types section if provided) */
  selectedDocumentType?: TabType;
  /** Callback when a document type is selected */
  onDocumentTypeSelect?: (type: TabType) => void;
  /** Badge counts for document types */
  documentTypeCounts?: Partial<Record<TabType, number>>;
}

/**
 * Renders a tree view of registries grouped by year.
 *
 * @example
 * <RegistryTree
 *   groups={groups}
 *   selectedRegistryId={selectedId}
 *   onRegistrySelect={handleSelect}
 *   invoiceCounts={counts}
 * />
 */
export function RegistryTree({
  groups,
  selectedRegistryId,
  onRegistrySelect,
  invoiceCounts,
  totalCount,
  isCountsLoading = false,
  searchQuery = "",
  defaultExpandedYears = [],
  selectedDocumentType,
  onDocumentTypeSelect,
  documentTypeCounts,
}: RegistryTreeProps) {
  const treeId = useId();

  // Determine if document types section should be shown
  const showDocumentTypes = selectedDocumentType !== undefined && onDocumentTypeSelect !== undefined;

  // Handle keyboard navigation for registry items
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent, registryId: string | null) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onRegistrySelect(registryId);
      }
    },
    [onRegistrySelect],
  );

  // Format year label for display
  const formatYearLabel = (year: number): string => {
    return year === 0 ? "Other" : year.toString();
  };

  // Check if group should be expanded by default
  const shouldExpandByDefault = (year: number): boolean => {
    // Expand if searching, or if year is in default expanded list
    // or if it's the current year
    if (searchQuery.length > 0) {
      return true;
    }
    if (defaultExpandedYears.includes(year)) {
      return true;
    }
    const currentYear = new Date().getFullYear();
    return year === currentYear;
  };

  return (
    <div role="tree" aria-label="Registry tree" className="space-y-1" data-testid="registry-tree">
      {/* Document Types section - shown when props are provided */}
      {showDocumentTypes && (
        <>
          <DocumentTypesSection
            selectedType={selectedDocumentType}
            onTypeSelect={onDocumentTypeSelect}
            counts={documentTypeCounts}
            isCountsLoading={isCountsLoading}
          />
          <div className="my-2 border-t border-gray-200 dark:border-gray-700" />
        </>
      )}

      {/* All Registries option */}
      <button
        type="button"
        role="treeitem"
        aria-selected={selectedRegistryId === null}
        aria-level={1}
        tabIndex={0}
        onClick={() => onRegistrySelect(null)}
        onKeyDown={(e) => handleKeyDown(e, null)}
        className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors ${
          selectedRegistryId === null
            ? "bg-blue-50 text-blue-900 dark:bg-blue-900/30 dark:text-blue-100"
            : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
        }`}
        data-testid="registry-tree-all"
      >
        <FolderOpen className="h-4 w-4 flex-shrink-0" />
        <span className="flex-1 truncate text-left font-medium">All Registries</span>
        {!isCountsLoading && totalCount !== undefined && (
          <span className="flex-shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-400">
            {totalCount}
          </span>
        )}
      </button>

      {/* Year groups */}
      {groups.map((group) => (
        <Disclosure key={group.year} defaultOpen={shouldExpandByDefault(group.year)} as="div" className="space-y-0.5">
          {({ open }) => (
            <>
              <DisclosureButton
                className="flex w-full items-center gap-1 rounded-md px-2 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                aria-controls={`${treeId}-group-${group.year}`}
                data-testid={`registry-tree-year-${group.year}`}
              >
                {open ? (
                  <ChevronDown className="h-4 w-4 flex-shrink-0 text-gray-500" />
                ) : (
                  <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-500" />
                )}
                <span className="flex-1 text-left">{formatYearLabel(group.year)}</span>
                {!isCountsLoading && (
                  <span className="flex-shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                    {group.totalCount}
                  </span>
                )}
              </DisclosureButton>
              <DisclosurePanel
                id={`${treeId}-group-${group.year}`}
                className="ml-3 space-y-0.5 border-l border-gray-200 pl-2 dark:border-gray-700"
                aria-label={`${formatYearLabel(group.year)} registries`}
              >
                {group.registries.map((registry) => (
                  <RegistryItem
                    key={registry.registryId}
                    registry={registry}
                    isSelected={selectedRegistryId === registry.registryId}
                    onSelect={() => onRegistrySelect(registry.registryId)}
                    onKeyDown={(e) => handleKeyDown(e, registry.registryId)}
                    invoiceCount={invoiceCounts.get(registry.registryId)}
                    isCountLoading={isCountsLoading}
                  />
                ))}
              </DisclosurePanel>
            </>
          )}
        </Disclosure>
      ))}

      {/* Empty state */}
      {groups.length === 0 && (
        <div
          className="px-2 py-4 text-center text-sm text-gray-500 dark:text-gray-400"
          data-testid="registry-tree-empty"
        >
          No registries found
        </div>
      )}
    </div>
  );
}

interface RegistryItemProps {
  registry: Registry;
  isSelected: boolean;
  onSelect: () => void;
  onKeyDown: (event: React.KeyboardEvent) => void;
  invoiceCount?: number;
  isCountLoading?: boolean;
}

function RegistryItem({
  registry,
  isSelected,
  onSelect,
  onKeyDown,
  invoiceCount,
  isCountLoading = false,
}: RegistryItemProps) {
  return (
    <button
      type="button"
      role="treeitem"
      aria-selected={isSelected}
      aria-level={2}
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={onKeyDown}
      className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors ${
        isSelected
          ? "bg-blue-50 text-blue-900 dark:bg-blue-900/30 dark:text-blue-100"
          : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
      }`}
      data-testid={`registry-tree-item-${registry.registryId}`}
    >
      <FileText className="h-4 w-4 flex-shrink-0" />
      <div className="flex flex-1 flex-col items-start overflow-hidden">
        <span className="w-full truncate font-medium">{registry.name}</span>
        <span className="w-full truncate text-xs text-gray-500 dark:text-gray-400">{registry.sequenceValue}</span>
      </div>
      {!isCountLoading && invoiceCount !== undefined && (
        <span className="flex-shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-400">
          {invoiceCount}
        </span>
      )}
    </button>
  );
}
